import { getAddMoreTinURL } from '@src/apps/Hitins/hiVolumeTinsFn';
import { checkIsInternal } from '@src/utility/createStorage';
import HttpService from '@src/utility/httpService';
import { config } from '@uhg-abyss/web/tools/config';
import { fetchTinGroupDetails } from './fetchTinGroupDetails';

// Re-export for backward compatibility
export { fetchTinGroupDetails };

const _httpService = new HttpService();

type TinRecord = Record<string, unknown>;

interface TinSearchResponse extends TinRecord {
  Data?: TinRecord | TinRecord[];
}

interface FetchTinsDetailsPayload {
  jobId?: string;
  tinSearchResponse?: TinSearchResponse;
}

interface PollingResponse {
  Data?: {
    Status?: string;
  };
}

interface SelectedTin {
  Tin?: string;
  selected?: boolean;
}

interface SaveCustomListPayload extends TinRecord {
  selectedOptions?: SelectedTin[];
  Tin?: string | SelectedTin[];
}

if (checkIsInternal()) {
  _httpService.setBaseURL(config('API_DOMAIN_INTERNAL')!);
} else {
  _httpService.setBaseURL(config('API_DOMAIN_EXTERNAL')!);
  //_httpService.setBaseURL(config('API_DOMAIN_INTERNAL'));
}

const updateCustomListCount = () => {
  const currentCount = sessionStorage.getItem('customListCount');
  const newCount = currentCount ? (parseInt(currentCount, 10) + 1).toString() : '1';
  sessionStorage.setItem('customListCount', newCount);
};

const getPreviousBaseURL = () => {
  return checkIsInternal() ? config('API_DOMAIN_INTERNAL') : config('API_DOMAIN_EXTERNAL');
};

export const fetchTinsDetailsService = async (payload: FetchTinsDetailsPayload) => {
  if (payload?.tinSearchResponse) {
    const data = payload.tinSearchResponse?.Data;
    return {
      ...payload.tinSearchResponse,
      Data: Array.isArray(data) ? { ValidTins: data, InvalidTins: [], NotFoundTins: [] } : data,
    };
  }

  const previousBaseURL = getPreviousBaseURL();
  let jobId = payload.jobId;
  if (!jobId) {
    const groupName = sessionStorage.getItem('groupName') || 'unsaved';
    const currentUser = JSON.parse(sessionStorage.getItem('currentUser') || '{}') as {
      MsId?: string;
      msId?: string;
    };
    const userId = currentUser?.MsId || currentUser?.msId;

    if (userId) {
      const groupDetails = await fetchTinGroupDetails(groupName, userId);
      if (groupDetails?.JobId) {
        jobId = groupDetails.JobId;
        sessionStorage.setItem('jobId', jobId);
      } else {
        console.error('Failed to fetch JobId for TIN group details');
        return null;
      }
    } else {
      console.error('User ID not found in session storage');
      return null;
    }
  }
  const url = `/extended-tins-jobs/result/${jobId}`;
  if (window.location.pathname !== '/myhome') {
    const poll = async (pollJobId: string): Promise<PollingResponse | null> => {
      _httpService.setBaseURL(config('BULK_PROCESSING_API') || '');
      try {
        _httpService.setBaseURL(config('BULK_PROCESSING_API') || '');
        const response = (await _httpService.get(`${url}`)) as PollingResponse;

        // If status is IN_PROGRESS, continue polling
        if (
          response &&
          response.Data &&
          typeof response.Data.Status === 'string' &&
          response.Data.Status === 'IN_PROGRESS'
        ) {
          // Wait 1 second before polling again
          return new Promise((resolve) => {
            setTimeout(() => resolve(poll(pollJobId)), 1000);
          });
        }
        return response;
      } catch (error) {
        console.error('Error in polling TINs details:', error);
        return null;
      }
    };
    try {
      return await poll(jobId);
    } finally {
      _httpService.setBaseURL(previousBaseURL || '');
    }
  } else {
    _httpService.setBaseURL(config('BULK_PROCESSING_API') || '');
    try {
      const responseData = await _httpService.get(url);
      return responseData;
    } catch {
      return { Data: [] };
    } finally {
      _httpService.setBaseURL(previousBaseURL || '');
    }
  }
};

export const fetchAddMoreTinsData = async (payload: TinRecord) => {
  const url = getAddMoreTinURL(payload);
  try {
    const responseData = await _httpService.push(url, payload);
    return responseData;
  } catch (error) {
    console.error('Error fetching add more TINs data:', error);
    return null;
  }
};

export const saveCustomListService = async (payload: SaveCustomListPayload) => {
  const previousBaseURL = checkIsInternal()
    ? config('API_DOMAIN_INTERNAL')
    : config('API_DOMAIN_EXTERNAL');
  _httpService.setBaseURL(config('BULK_PROCESSING_API') || '');

  // Format Tin as array of objects [{ Tin, Selected }]
  const formattedPayload = { ...payload };
  if (payload.selectedOptions && Array.isArray(payload.selectedOptions)) {
    formattedPayload.Tin = payload.selectedOptions.map((opt) => ({
      Tin: opt?.Tin,
      Selected: opt?.selected,
    }));
    delete formattedPayload.selectedOptions;
  } else if (typeof payload.Tin === 'string') {
    formattedPayload.Tin = payload.Tin.split(',').map((tin: string) => ({
      Tin: tin.trim(),
      Selected: true,
    }));
  }

  const url = '/saved-tins-bulk-process/save-tins';
  try {
    updateCustomListCount();
    const responseData = await _httpService.push(url, formattedPayload);
    return responseData;
  } catch (error) {
    console.error('Error saving custom list:', error);
    return null;
  } finally {
    _httpService.setBaseURL(previousBaseURL || '');
  }
};

export const addSingleTinToList = async (payload: TinRecord) => {
  const url = '/addTinToList';
  try {
    const responseData = await _httpService.push(url, payload);
    return responseData;
  } catch (error) {
    console.error('Error adding single TIN to list:', error);
    return null;
  }
};
